import { skipToken, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { api, ApiError } from '@/api/client';
import type { components } from '@/api/schema';
import type { SearchOption } from '@/components/SearchSelect';
import { quantity } from '@/components/ui';
import {
  nameSearchTerm,
  naturalCompare,
  skuCodeMatches,
  skuCodeRank,
  skuNameMatches,
  skuNameRank,
} from '@/components/itemMatch';
import { isGatewayFailure, useConnection } from '@/stores/connection';
import { savedWork } from '@/offline/engine';
import { pendingRows, usePendingRows, withPending } from '@/offline/pending';

/**
 * Data hooks shared by every Inventory & Stores screen. These used to be
 * copied into each screen file, which is how the SKU picker ended up capped
 * at 200 items in four places at once.
 */

export type SkuRow = components['schemas']['SkuListItemDto'];
export type StoreRow = components['schemas']['StoreDto'];
export type StorageBinRow = components['schemas']['StorageBinDto'];

const SEARCH_PAGE = 20;
/** Rows fetched for a code-only or name-only search, before the rows that
 *  matched on the other field are dropped. */
const FIELD_SEARCH_FETCH = 50;
/** A search's answer is only wanted while the box is in use. */
const SEARCH_GC_MS = 5 * 60_000;

/**
 * Every active item, loaded by the warm-up while there is a line (.docs/023),
 * so the item pickers can still search with none.
 */
export const ALL_SKUS_KEY = ['inv', 'skus', 'all'] as const;

/** The server could not be reached: no line, or the proxy has no API behind it. */
function noLine(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 0 || isGatewayFailure(error.status));
}

/**
 * Searches items on this PC the way the server does: a box searching one
 * field matches that field alone; with none, code or name. Closest first.
 */
export function findItems(rows: readonly SkuRow[], query: string, by?: ItemSearchField): SkuRow[] {
  const typed = query.trim();
  if (!typed) return [...rows].sort((a, b) => naturalCompare(a.code, b.code)).slice(0, SEARCH_PAGE);
  if (by) return onField([...rows], typed, by).slice(0, SEARCH_PAGE);
  const byCode = onField([...rows], typed, 'code');
  const seen = new Set(byCode.map((s) => s.id));
  const byName = onField([...rows], typed, 'name').filter((s) => !seen.has(s.id));
  return [...byCode, ...byName].slice(0, SEARCH_PAGE);
}

/** How much of each item a store held when the warm-up last read it, or
 *  null when that store was never read. */
function heldOnThisPc(queryClient: QueryClient, storeId: string, skuIds: readonly string[]): Map<string, number> | null {
  const prefix = ['inv', 'balances', 'onhand', storeId];
  if (queryClient.getQueriesData({ queryKey: prefix }).length === 0) return null;
  const held = new Map<string, number>();
  for (const id of skuIds) {
    const qty = queryClient.getQueryData<number>([...prefix, id, null]);
    if (qty) held.set(id, qty);
  }
  return held;
}

/** Invalidates everything a stock movement changes. */
export function useStockInvalidation() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['inv'] });
  }, [queryClient]);
}

export function useStores() {
  return useQuery({
    queryKey: ['stores'],
    queryFn: () => api.get('/api/v1/stores'),
  });
}

/**
 * The active storage bins of one store. Empty for a store that does not use
 * bins — which is how every form decides to show no bin field at all.
 */
export function useStorageBins(storeId: string | undefined) {
  return useQuery({
    queryKey: ['storage-bins', 'store', storeId ?? null],
    queryFn: () => api.get('/api/v1/storage-bins', { storeId }),
    enabled: Boolean(storeId),
  });
}

/**
 * The active storage bins of each of several stores, keyed by store id — for
 * a form whose lines can each go to a different store. A store still loading
 * is missing from the map.
 */
export function useBinsByStore(storeIds: readonly string[]): ReadonlyMap<string, StorageBinRow[]> {
  const ids = [...new Set(storeIds.filter(Boolean))].sort();
  const results = useQueries({
    queries: ids.map((storeId) => ({
      queryKey: ['storage-bins', 'store', storeId],
      queryFn: () => api.get('/api/v1/storage-bins', { storeId }),
    })),
  });
  const byStore = new Map<string, StorageBinRow[]>();
  results.forEach((r, i) => {
    if (r.data) byStore.set(ids[i]!, r.data);
  });
  return byStore;
}

/**
 * Every supplier, with any added on this PC and not yet sent after them
 * (src/offline/pending.ts), so a receipt typed offline can name one at once.
 */
export function useSuppliers() {
  const query = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get('/api/v1/suppliers'),
  });
  const pending = usePendingRows('supplier.create');
  const data = useMemo(() => withPending(query.data, pending), [query.data, pending]);
  return { ...query, data };
}

/** The active categories, with any added on this PC and not yet sent. */
export function useCategories() {
  const query = useQuery({
    queryKey: ['inv', 'categories', false],
    queryFn: () => api.get('/api/v1/inventory/categories'),
  });
  const pending = usePendingRows('category.create');
  const data = useMemo(() => withPending(query.data, pending), [query.data, pending]);
  return { ...query, data };
}

/** The active units, with any added on this PC and not yet sent. */
export function useUoms() {
  const query = useQuery({
    queryKey: ['inv', 'uoms', false],
    queryFn: () => api.get('/api/v1/inventory/uoms'),
  });
  const pending = usePendingRows('uom.create');
  const data = useMemo(() => withPending(query.data, pending), [query.data, pending]);
  return { ...query, data };
}

/**
 * Marks that a store's whole holding was read onto this PC (src/offline/warm.ts),
 * so with no line an item missing from it is known to be held at zero — not
 * "still loading" for ever.
 */
export function holdingReadKey(storeId: string) {
  return ['inv', 'holding-read', storeId] as const;
}

/** True offline, for a store whose whole holding this PC has read. */
function heldNoneOffline(queryClient: QueryClient, storeId: string): boolean {
  return !useConnection.getState().online && queryClient.getQueryData(holdingReadKey(storeId)) !== undefined;
}

/** Lots of an item held in a store, oldest received first (FIFO). */
export function useBatches(storeId: string, skuId: string, enabled = true) {
  const queryClient = useQueryClient();
  const online = useConnection((s) => s.online);
  return useQuery({
    queryKey: ['inv', 'batches', storeId, skuId],
    queryFn: () => api.get('/api/v1/inventory/batches', { storeId, skuId }),
    enabled: enabled && Boolean(storeId && skuId),
    // Offline, an item the warm-up did not find in the store holds no lots there.
    ...(!online && heldNoneOffline(queryClient, storeId) ? { placeholderData: [] } : {}),
  });
}

/**
 * Previews the code a create form would be assigned, without reserving it —
 * the code actually saved can differ if another creation happens first, so
 * the saved record is the source of truth, not this preview.
 */
export function useCodePreview(entity: string, enabled: boolean) {
  return useQuery({
    queryKey: ['codes', 'next', entity],
    queryFn: () => api.get('/api/v1/codes/next', { entity }),
    enabled,
    staleTime: 0,
  });
}

const LABEL_SEPARATOR = ' — ';

export function skuLabel(sku: Pick<SkuRow, 'code' | 'nameEn'>): string {
  return `${sku.code}${LABEL_SEPARATOR}${sku.nameEn}`;
}

/** Reads `skuLabel` back into its code and name. Codes never hold the
 *  separator, so the first one splits them even when a name holds another. */
export function splitSkuLabel(label: string): { code: string; name: string } {
  const at = label.indexOf(LABEL_SEPARATOR);
  return at < 0 ? { code: '', name: label } : { code: label.slice(0, at), name: label.slice(at + LABEL_SEPARATOR.length) };
}

/** Which part of an item a picker box searches. */
export type ItemSearchField = 'code' | 'name';

/** The rows of a search that match on `by` alone, closest first. */
function onField(rows: SkuRow[], query: string, by: ItemSearchField): SkuRow[] {
  if (by === 'code') {
    return rows
      .filter((s) => skuCodeMatches(s.code, query))
      .sort((a, b) => skuCodeRank(a.code, query) - skuCodeRank(b.code, query) || naturalCompare(a.code, b.code));
  }
  return rows
    .filter((s) => skuNameMatches(s, query))
    .sort(
      (a, b) =>
        skuNameRank(a.nameEn, query) - skuNameRank(b.nameEn, query) ||
        naturalCompare(a.nameEn, b.nameEn) ||
        naturalCompare(a.code, b.code),
    );
}

/**
 * Every item this PC holds, by id, as the warm-up last read them: what a
 * stock list or dialog needs to show its quantities in packages. An item not
 * there yet just shows plain quantities.
 */
export function useItemsById(): ReadonlyMap<string, SkuRow> {
  // Only watched, never fetched here: the warm-up fills it.
  const { data } = useQuery<SkuRow[]>({ queryKey: ALL_SKUS_KEY, queryFn: skipToken });
  return useMemo(() => new Map((data ?? []).map((s) => [s.id, s])), [data]);
}

/** "BIN01 — Rack A", the way a bin is named everywhere it is picked. */
export function binLabel(bin: Pick<StorageBinRow, 'code' | 'nameEn'>): string {
  return `${bin.code} — ${bin.nameEn}`;
}

/**
 * Server-backed SKU search for a `SearchSelect`, plus a record of every SKU
 * seen so far — a form needs the chosen item's unit and tracking flags after
 * the search result that returned it has been replaced by the next one.
 *
 * With a `storeId`, each option also shows how much of it that store holds,
 * so issuing or moving stock starts from what is actually there.
 */
export function useSkuCatalog(storeId?: string) {
  const queryClient = useQueryClient();
  const [known, setKnown] = useState<ReadonlyMap<string, SkuRow>>(() => new Map());

  const remember = useCallback((rows: SkuRow[]) => {
    if (rows.length === 0) return;
    setKnown((prev) => {
      const next = new Map(prev);
      let changed = false;
      for (const row of rows) {
        if (next.get(row.id) !== row) {
          next.set(row.id, row);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const search = useCallback(
    async (query: string, by?: ItemSearchField): Promise<SearchOption[]> => {
      // The server matches code and name alike, so a box searching one of
      // them asks for more and keeps only the rows that match on its own
      // field — a name that happens to contain "8" is no answer to "8"
      // typed as a code.
      const take = by ? FIELD_SEARCH_FETCH : SEARCH_PAGE;
      const asked = by === 'name' ? nameSearchTerm(query) : query;
      // With no line, the items on this PC. Never a request: offline, one
      // would wait for the line to come back, "Searching…" all the while.
      const onPc = queryClient.getQueryData<SkuRow[]>(ALL_SKUS_KEY);
      let offline = !useConnection.getState().online;
      let rows: SkuRow[] = [];
      if (!offline) {
        try {
          const skus = await queryClient.fetchQuery({
            queryKey: ['inv', 'skus', 'search', asked, take],
            queryFn: () => api.get('/api/v1/inventory/skus', { search: asked || undefined, take }),
            staleTime: 30_000,
            gcTime: SEARCH_GC_MS,
          });
          rows = by ? onField(skus.items, query, by).slice(0, SEARCH_PAGE) : skus.items;
        } catch (error) {
          if (!noLine(error) || !onPc) throw error;
          offline = true;
        }
      }
      if (offline) rows = findItems(onPc ?? [], query, by);
      // Items added on this PC and not sent yet can be picked at once.
      const pendingItems = findItems(pendingRows(await savedWork(), 'sku.create'), query, by);
      if (pendingItems.length) rows = [...pendingItems, ...rows].slice(0, SEARCH_PAGE);
      remember(rows);

      let onHand: Map<string, number> | null = null;
      if (storeId && offline) {
        onHand = heldOnThisPc(queryClient, storeId, rows.map((s) => s.id));
      } else if (storeId) {
        const balances = await queryClient
          .fetchQuery({
          queryKey: ['inv', 'balances', 'search', storeId, query],
          queryFn: () =>
            api.get('/api/v1/inventory/balances', {
              storeId,
              search: query || undefined,
              take: 200,
            }),
          staleTime: 10_000,
          gcTime: SEARCH_GC_MS,
        })
          // The line went between the two requests: what this PC last read.
          .catch((error: unknown) => {
            if (noLine(error)) return null;
            throw error;
          });
        if (balances) {
          onHand = new Map();
          for (const b of balances.items) {
            onHand.set(b.skuId, (onHand.get(b.skuId) ?? 0) + Number(b.qty));
          }
        } else {
          onHand = heldOnThisPc(queryClient, storeId, rows.map((s) => s.id));
        }
      }

      return rows.map((s) => {
        const held = onHand?.get(s.id);
        return {
          value: s.id,
          label: skuLabel(s),
          meta: onHand
            ? held
              ? `${quantity(String(held), s.uomPrecision)} ${s.uom} here`
              : 'none here'
            : s.uom,
        };
      });
    },
    [queryClient, remember, storeId],
  );

  /**
   * Loads the SKUs a draft or a prefilled form already references, so their
   * units and tracking flags are known before anyone searches. Deleted items
   * are included: a draft may hold one deleted since.
   */
  const loadByIds = useCallback(
    async (ids: string[]) => {
      const all = [...new Set(ids.filter(Boolean))].sort();
      if (all.length === 0) return [];
      // Items added on this PC are read from what waits to be sent.
      const local = all.filter((id) => id.startsWith('local:'));
      const unique = all.filter((id) => !id.startsWith('local:'));
      const pendingFound = local.length ? pendingRows(await savedWork(), 'sku.create').filter((s) => local.includes(s.id)) : [];
      remember(pendingFound);
      if (unique.length === 0) return pendingFound;
      const fromPc = () => {
        const onPc = new Map((queryClient.getQueryData<SkuRow[]>(ALL_SKUS_KEY) ?? []).map((s) => [s.id, s]));
        const found = unique.flatMap((id) => onPc.get(id) ?? []);
        remember(found);
        return found;
      };
      if (!useConnection.getState().online) return [...pendingFound, ...fromPc()];
      try {
        const result = await queryClient.fetchQuery({
          queryKey: ['inv', 'skus', 'ids', unique.join(',')],
          queryFn: () =>
            api.get('/api/v1/inventory/skus', { ids: unique.join(','), take: unique.length }),
          staleTime: 30_000,
        });
        remember(result.items);
        return [...pendingFound, ...result.items];
      } catch (error) {
        // The line went while asking: what this PC has, as offline.
        if (noLine(error)) return [...pendingFound, ...fromPc()];
        throw error;
      }
    },
    [queryClient, remember],
  );

  /**
   * Resolves a list of item codes in one request — what a pasted shipment
   * list needs instead of one search per line. Case does not matter and a
   * deleted item still resolves, matching `loadByIds`.
   */
  const loadByCodes = useCallback(
    async (codes: string[]) => {
      const unique = [...new Set(codes.map((c) => c.trim()).filter(Boolean))];
      if (unique.length === 0) return [];
      const fromPc = () => {
        const wanted = new Set(unique.map((c) => c.toUpperCase()));
        const found = (queryClient.getQueryData<SkuRow[]>(ALL_SKUS_KEY) ?? []).filter((s) => wanted.has(s.code.toUpperCase()));
        remember(found);
        return found;
      };
      if (!useConnection.getState().online) return fromPc();
      try {
        const result = await queryClient.fetchQuery({
          queryKey: ['inv', 'skus', 'codes', unique.map((c) => c.toUpperCase()).sort().join(',')],
          queryFn: () => api.get('/api/v1/inventory/skus', { codes: unique.join(','), take: unique.length }),
          staleTime: 30_000,
        });
        remember(result.items);
        return result.items;
      } catch (error) {
        if (noLine(error)) return fromPc();
        throw error;
      }
    },
    [queryClient, remember],
  );

  return { known, search, remember, loadByIds, loadByCodes };
}

export type LastCost = components['schemas']['LastCostDto'];

/**
 * The last purchase cost of each item, for prefilling a receive line so a
 * blank cost box never silently posts as zero.
 *
 * Deliberately not scoped to one store: the same item usually costs the same
 * wherever it is received, and asking "what did we pay anywhere" finds a
 * price far more often than "what did we pay into this exact store."
 *
 * `lastCost` on a returned row can be `undefined` (stripped for a caller
 * without inv.price.read — nothing to prefill or show) or `null` (the item
 * really has no purchase history yet) as well as a real amount; callers must
 * tell those two apart rather than treating both as "no data".
 */
export function useLastCosts(
  skuIds: string[],
  enabled = true,
  /** Item and supplier pairs, for what that supplier charged last as well. */
  pairs: ReadonlyArray<{ skuId: string; supplierId: string }> = [],
) {
  const ids = [...new Set(skuIds.filter(Boolean))].sort();
  const key = ids.join(',');
  const pairKey = [
    ...new Set(pairs.filter((p) => p.skuId && /^\d+$/.test(p.supplierId)).map((p) => `${p.skuId}:${p.supplierId}`)),
  ]
    .sort()
    .join(',');
  return useQuery({
    queryKey: ['inv', 'last-costs', key, pairKey],
    queryFn: () =>
      api.get('/api/v1/inventory/last-costs', { skuIds: key, ...(pairKey ? { pairs: pairKey } : {}) }),
    enabled: enabled && ids.length > 0,
    staleTime: 30_000,
  });
}

/** How much of an item a store holds, summed across lots unless one is named. */
export function useOnHand(storeId: string, skuId: string, batchId?: string) {
  const queryClient = useQueryClient();
  const online = useConnection((s) => s.online);
  return useQuery({
    // Offline, an item the warm-up did not find in the store is held at zero.
    ...(!online && heldNoneOffline(queryClient, storeId) ? { placeholderData: 0 } : {}),
    queryKey: ['inv', 'balances', 'onhand', storeId, skuId, batchId ?? null],
    queryFn: async () => {
      const result = await api.get('/api/v1/inventory/balances', {
        storeId,
        skuId,
        batchId: batchId || undefined,
        take: 200,
      });
      return result.items.reduce((sum, b) => sum + Number(b.qty), 0);
    },
    enabled: Boolean(storeId && skuId),
  });
}

/**
 * A key identifying one attempt at submitting a form. A retried submit from a
 * poor connection reuses it, so the server recognises the retry instead of
 * receiving the lorry twice; a genuinely new document gets a fresh one.
 */
export function newIdempotencyKey(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
